import { ChevronDown } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button as AriaButton, Disclosure, DisclosurePanel } from 'react-aria-components';
import type { BulkTask } from '../../../api/types';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { Money } from '../../../ui/Layout';
import styles from './Batch.module.css';
import { BATCH_MAX, type BatchItem } from './batchStore';
import { taskTitle } from './text';

/**
 * «الحزمة» (BLOCK-1B-BULK-DESIGN §1, AC-B4, AC-B10): the named batch beside the steps. Its heading
 * holds no number; each document is named, with «أخرِج»; the room left and «إفراغ الحزمة» sit on
 * lines of their own at the foot. Its only controls are «أخرِج» and «إفراغ الحزمة»: no input, select,
 * checkbox, switch, toggle, tab or run button, and it never collapses on desktop.
 */
export function BatchRail({
  task,
  items,
  locked,
  onTakeOut,
  onClear,
}: {
  task: BulkTask | null;
  items: readonly BatchItem[];
  /** A run is in progress: nothing may leave the batch. */
  locked: boolean;
  /** Takes a document out; `focusAfter` is the element that should take focus next. */
  onTakeOut: (item: BatchItem) => void;
  onClear: () => void;
}) {
  const { t, formatNumber } = useI18n();
  const listRef = useRef<HTMLOListElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  // RIB-08: does the list hold more items below its visible end (a scroll cue)?
  const [more, setMore] = useState(false);
  const measure = useCallback(() => {
    const list = listRef.current;
    setMore(Boolean(list && list.scrollTop + list.clientHeight < list.scrollHeight - 2));
  }, []);
  useLayoutEffect(() => {
    measure();
    const list = listRef.current;
    const observer =
      list && typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    if (list) observer?.observe(list);
    return () => observer?.disconnect();
  }, [measure, items.length]);

  const takeOut = (item: BatchItem, index: number) => {
    // Focus moves to the next item's button, else the previous one, else the rail heading.
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>(
      '[data-testid="batch-item-out"]',
    );
    const next = buttons?.[index + 1] ?? buttons?.[index - 1] ?? null;
    onTakeOut(item);
    requestAnimationFrame(() => (next ?? headingRef.current)?.focus());
  };

  return (
    <aside className={styles.rail} aria-labelledby="batch-rail-title" data-testid="batch-rail">
      <h2 id="batch-rail-title" ref={headingRef} tabIndex={-1} className={styles.railTitle}>
        {t.batch.rail.title}
      </h2>
      {task ? (
        <p className={styles.railTask} data-testid="batch-rail-task">
          {t.batch.rail.task(taskTitle(t, task))}
        </p>
      ) : null}
      {items.length ? (
        <ol
          ref={listRef}
          className={styles.railList}
          data-testid="batch-list"
          data-more={more}
          onScroll={measure}
        >
          {items.map((item, index) => (
            <li
              key={item.id}
              className={styles.railItem}
              data-testid={`batch-item-${item.number ?? item.id}`}
            >
              <span className={styles.railNumber} dir="ltr">
                {item.number ?? '—'}
              </span>
              <span className={styles.railBuyer}>
                <UserText>
                  {item.cash ? t.sales.cash : (item.receiverName ?? t.sales.noCustomer)}
                </UserText>
              </span>
              <span className={styles.railAmount}>
                <Money value={item.totalAmount} currency="EGP" />
              </span>
              <Button
                variant="ghost"
                size="sm"
                className={styles.textButton}
                aria-label={t.batch.rail.takeOutNamed(item.number ?? '')}
                isDisabled={locked}
                onPress={() => takeOut(item, index)}
                testId="batch-item-out"
              >
                {t.batch.rail.takeOut}
              </Button>
            </li>
          ))}
        </ol>
      ) : (
        <p className={styles.railEmpty} data-testid="batch-rail-empty">
          {t.batch.rail.empty}
        </p>
      )}
      <p
        className={styles.room}
        data-testid="batch-room"
        data-count={items.length}
        data-room={BATCH_MAX - items.length}
      >
        {t.batch.rail.room(formatNumber(String(BATCH_MAX - items.length)))}
      </p>
      {items.length ? (
        <div className={styles.clearLine}>
          <Button
            variant="ghost"
            size="sm"
            className={styles.textButton}
            isDisabled={locked}
            onPress={onClear}
            testId="batch-clear"
          >
            {t.batch.rail.clear}
          </Button>
        </div>
      ) : null}
    </aside>
  );
}

/**
 * Below 64rem the rail opens under the step list from this toggle (a React Aria `Disclosure`; the
 * toggle sits outside `batch-rail`): «الحزمة» with a chevron, no number and no clear control (AC-B4).
 */
export function BatchRailToggle({
  isExpanded,
  onExpandedChange,
  children,
}: {
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  children: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <Disclosure
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
      className={styles.railDisclosure}
    >
      <AriaButton slot="trigger" className={styles.railToggle} data-testid="batch-rail-toggle">
        <span>{t.batch.rail.title}</span>
        <ChevronDown size={16} aria-hidden="true" className={styles.railChevron} />
      </AriaButton>
      <DisclosurePanel>{children}</DisclosurePanel>
    </Disclosure>
  );
}
